import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Hero from "@/components/sections/Hero";
import About from "@/components/sections/About";
import Gallery from "@/components/sections/Gallery";
import InteractiveMap from "@/components/sections/InteractiveMap";
import Download from "@/components/sections/Download";
import Addons from "@/components/sections/Addons";
import Archive from "@/components/sections/Archive";
import SocialMedia from "@/components/sections/SocialMedia";
import Team from "@/components/sections/Team";
import FAQ from "@/components/sections/FAQ";
import CommunityScreenshots from "@/components/sections/CommunityScreenshots";
import PlateGeneratorBanner from "@/components/sections/PlateGeneratorBanner";
import SEO, {
    buildHomeJsonLd,
    buildFaqJsonLd,
    buildBreadcrumbJsonLd,
    buildImageGalleryJsonLd,
    buildTeamJsonLd,
} from "@/components/SEO";
import LiveVisitorsChip from "@/components/LiveVisitorsChip";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { api } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";

export default function Home() {
    const [modInfo, setModInfo] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [pois, setPois] = useState([]);
    const [socials, setSocials] = useState([]);
    const [team, setTeam] = useState([]);
    const [addons, setAddons] = useState([]);
    const [faq, setFaq] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPoiId, setSelectedPoiId] = useState(null);
    const location = useLocation();
    const params = useParams();

    // Detect deep-linked POI: either path /poi/:slugOrId or ?poi=<slugOrId>.
    // The URL may contain a slug ("ruda-centrum") OR a legacy UUID - we accept both.
    const initialPoiKey = useMemo(() => {
        if (params?.poiId) return params.poiId;
        const sp = new URLSearchParams(location.search);
        return sp.get("poi") || null;
    }, [params?.poiId, location.search]);

    useEffect(() => {
        trackPageview(initialPoiKey ? `/poi/${initialPoiKey}` : "/");
    }, [initialPoiKey]);

    useEffect(() => {
        (async () => {
            try {
                const [m, g, p, s, t, a, f] = await Promise.all([
                    api.get("/mod-info"),
                    api.get("/gallery"),
                    api.get("/poi"),
                    api.get("/social"),
                    api.get("/team"),
                    api.get("/addons"),
                    api.get("/faq"),
                ]);
                setModInfo(m.data);
                setGallery(g.data);
                setPois(p.data);
                setSocials(s.data);
                setTeam(t.data);
                setAddons(a.data);
                setFaq(f.data);
            } catch (e) {
                console.error("Failed to load home data", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Handle hash scroll after data load. If deep-linked POI is requested,
    // also scroll the map section into view once POIs are loaded.
    useEffect(() => {
        if (initialPoiKey && pois.length) {
            setTimeout(() => {
                const el = document.getElementById("mapa");
                if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 250);
            return;
        }
        if (location.hash) {
            const id = location.hash.replace("#", "");
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth" });
            }, 200);
        }
    }, [location.hash, modInfo, initialPoiKey, pois.length]);

    // Resolve the currently-selected POI. Selection from in-map clicks is
    // identified by POI id. Deep-link may be slug OR id - we match on both.
    const selectedPoi =
        pois.find((p) => p.id === selectedPoiId) ||
        pois.find(
            (p) => p.id === initialPoiKey || p.slug === initialPoiKey
        ) ||
        null;

    // Build dynamic SEO tags when a POI is selected (for shareability).
    // We always use the slug (when available) for canonical/share URLs so the
    // link is human-readable. Legacy POIs without slug fall back to id.
    const poiUrlKey = selectedPoi ? selectedPoi.slug || selectedPoi.id : null;
    const seoProps = selectedPoi
        ? {
              title: selectedPoi.title,
              description:
                  selectedPoi.description ||
                  `Punkt na interaktywnej mapie Project Ruda Śląska - fanowskiej mapy 1:1 do ETS2.`,
              image: selectedPoi.image_url || undefined,
              pathname: `/poi/${poiUrlKey}`,
              type: "place",
          }
        : { pathname: "/", type: "website" };

    const poiJsonLd = selectedPoi
        ? [
              {
                  "@context": "https://schema.org",
                  "@type": "Place",
                  name: selectedPoi.title,
                  description:
                      selectedPoi.description ||
                      "Punkt na interaktywnej mapie Project Ruda Śląska.",
                  url:
                      (modInfo?.seo_site_url || "https://projectrs.pl").replace(
                          /\/$/,
                          ""
                      ) + `/poi/${poiUrlKey}`,
                  ...(selectedPoi.image_url
                      ? { image: selectedPoi.image_url }
                      : {}),
              },
          ]
        : [];

    return (
        <div className="App" data-testid="home-page">
            <SEO
                modInfo={modInfo}
                {...seoProps}
                jsonLd={
                    selectedPoi
                        ? poiJsonLd
                        : [
                              ...buildHomeJsonLd({ modInfo, socials }),
                              ...(faq.length ? [buildFaqJsonLd(faq)] : []),
                              ...(gallery.length
                                  ? [
                                        buildImageGalleryJsonLd({
                                            siteUrl: modInfo?.seo_site_url || "",
                                            images: gallery,
                                        }),
                                    ]
                                  : []),
                              ...(team.length
                                  ? [
                                        buildTeamJsonLd({
                                            siteUrl: modInfo?.seo_site_url || "",
                                            members: team,
                                        }),
                                    ]
                                  : []),
                              buildBreadcrumbJsonLd([
                                  {
                                      name: "Home",
                                      url: `${(modInfo?.seo_site_url || "https://projectrs.pl").replace(/\/$/, "")}/`,
                                  },
                              ]),
                          ]
                }
            />
            <Header />
            <main>
                <Hero modInfo={modInfo} />
                <About modInfo={modInfo} />
                <Gallery images={gallery} loading={loading} />
                <PlateGeneratorBanner />
                <InteractiveMap
                    mapImageUrl={modInfo?.map_image_url}
                    pois={pois}
                    initialPoiKey={initialPoiKey}
                    onSelectionChange={setSelectedPoiId}
                    siteUrl={modInfo?.seo_site_url || ""}
                />
                <Download modInfo={modInfo} />
                <Addons addons={addons} loading={loading} />
                <Archive loading={loading} />
                <Team members={team} loading={loading} />
                <CommunityScreenshots />
                <FAQ items={faq} loading={loading} />
                <SocialMedia links={socials} />
            </main>
            <Footer />
            <LiveVisitorsChip />
            <PwaInstallPrompt />
        </div>
    );
}
